import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
import _, { isEqual } from 'lodash';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, FormBase } from '../Interface';
import { cleanValues, generateAutocompleteItems, getJokerFieldType } from '../../utils/helper';
import OtherExpenseReports from './OtherExpenseReports';
import OtherExpenseTabs from './OtherExpenseTabs';
import OtherExpenseFields from './OtherExpenseFields';
import otherExpenseService from '../../service/otherExpense.service';
import useParam from '../../context/ParamContext';
import jokerService from '../../service/joker.service';
import { JokerFieldTypes, JokerTypes } from '../../config/joker';

export const OtherExpensePage = () => {
  const { getUser, user } = useUsers();
  const { getParam } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [submenu, setSubmenu] = useState(null);
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [canEdit, setCanEdit] = useState(true);
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    company: [],
    costCenter: [],
    costAndRevenue: [],
    employee: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerLineFields, setJokerLineFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const defaultOtherExpenseLine = {
    id: null,
    isNew: true,
    costAndRevenueTypeId: null,
    employeeId: null,
    name: '',
    amount: null,
    costCenterCode: '',
    projectId: null,
    companyId: null,
    reference: '',
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
  };

  const def = {
    date: dayjs(new Date()),
    projectId: null,
    costAndRevenueTypeId: null,
    costCenterCode: null,
    projectOrderNumber: '',
    otherExpenseLine: [],
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
  };

  const getJokerFields = () => {
    jokerService
      .getFields(JokerTypes.OTHEREXPENSE)
      .then((data) => {
        const [headFields, lineFields] = data.reduce(
          (acc, field) => {
            if (field.isRow) {
              acc[1].push(field);
            } else {
              acc[0].push(field);
            }
            return acc;
          },
          [[], []]
        );

        setJokerFields(headFields);
        setJokerLineFields(lineFields);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getMenus();
    getParam();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
    getJokerFields();
  }, []);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('otherExpense');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costCenterToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );
    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costAndRevenueToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          company: resp[1],
          costCenter: resp[2],
          costAndRevenue: resp[3],
          employee: resp[4],
        };

        setAutoCompleteData(aData);

        const filteredEmployee = aData.employee.filter((employee) => employee.active);
        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          project: generateAutocompleteItems(filteredProject, ['projectName', 'orderNumber'], 'projectId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          costCenter: generateAutocompleteItems(aData.costCenter, 'name', 'code'),
          costAndRevenue: generateAutocompleteItems(aData.costAndRevenue, 'name', 'id'),
          employee: generateAutocompleteItems(filteredEmployee, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const valuesToForm = () => {
    const {
      id,
      date,
      projectId,
      costAndRevenueTypeId,
      costCenterCode,
      joker_char1,
      joker_char2,
      joker_char3,
      joker_char4,
      joker_char5,
      joker_char6,
      joker_char7,
      joker_char8,
      joker_char9,
      joker_char10,
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1,
      joker_num2,
      joker_num3,
      joker_num4,
      joker_num5,
      joker_int1,
      joker_int2,
      joker_int3,
      joker_int4,
      joker_int5,
      otherExpenseLine,
    } = selectedValues;

    setInitValues({
      id,
      date: dayjs(date),
      projectId,
      costAndRevenueTypeId,
      costCenterCode,
      joker_char1: joker_char1 ?? '',
      joker_char2: joker_char2 ?? '',
      joker_char3: joker_char3 ?? '',
      joker_char4: joker_char4 ?? '',
      joker_char5: joker_char5 ?? '',
      joker_char6: joker_char6 ?? '',
      joker_char7: joker_char7 ?? '',
      joker_char8: joker_char8 ?? '',
      joker_char9: joker_char9 ?? '',
      joker_char10: joker_char10 ?? '',
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1: joker_num1 ?? '',
      joker_num2: joker_num2 ?? '',
      joker_num3: joker_num3 ?? '',
      joker_num4: joker_num4 ?? '',
      joker_num5: joker_num5 ?? '',
      joker_int1: joker_int1 ?? '',
      joker_int2: joker_int2 ?? '',
      joker_int3: joker_int3 ?? '',
      joker_int4: joker_int4 ?? '',
      joker_int5: joker_int5 ?? '',
      projectOrderNumber: selectedValues.project?.orderNumber || '',
      otherExpenseLine: otherExpenseLine?.map((oE) => ({
        id: oE.id,
        costAndRevenueTypeId: oE.costAndRevenueTypeId,
        employeeId: oE.employeeId,
        name: oE.name,
        amount: oE.amount,
        costCenterCode: oE.costCenterCode,
        projectId: oE.projectId,
        companyId: oE.companyId,
        reference: oE.reference,
        createdBy: oE.createdBy,
        relatedMasterDataUrl: oE?.costAndRevenueType?.relatedMasterDataUrl ?? null,
        joker_char1: oE.joker_char1 ?? '',
        joker_char2: oE.joker_char2 ?? '',
        joker_char3: oE.joker_char3 ?? '',
        joker_char4: oE.joker_char4 ?? '',
        joker_char5: oE.joker_char5 ?? '',
        joker_char6: oE.joker_char6 ?? '',
        joker_char7: oE.joker_char7 ?? '',
        joker_char8: oE.joker_char8 ?? '',
        joker_char9: oE.joker_char9 ?? '',
        joker_char10: oE.joker_char10 ?? '',
        joker_date1: oE.joker_date1,
        joker_date2: oE.joker_date2,
        joker_date3: oE.joker_date3,
        joker_date4: oE.joker_date4,
        joker_date5: oE.joker_date5,
        joker_num1: oE.joker_num1 ?? '',
        joker_num2: oE.joker_num2 ?? '',
        joker_num3: oE.joker_num3 ?? '',
        joker_num4: oE.joker_num4 ?? '',
        joker_num5: oE.joker_num5 ?? '',
        joker_int1: oE.joker_int1 ?? '',
        joker_int2: oE.joker_int2 ?? '',
        joker_int3: oE.joker_int3 ?? '',
        joker_int4: oE.joker_int4 ?? '',
        joker_int5: oE.joker_int5 ?? '',
      })),
    });
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(),
    projectId: Yup.string().nullable(),
    costAndRevenueTypeId: Yup.string().required(),
    costCenterCode: Yup.string().nullable(),
    projectOrderNumber: Yup.string(),
    ...(jokerFields.length > 0
      ? Object.fromEntries(
          jokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
    otherExpenseLine: Yup.array().of(
      Yup.object().shape({
        costAndRevenueTypeId: Yup.string().required(),
        employeeId: Yup.string()
          .nullable()
          .when('relatedMasterDataUrl', {
            is: 'employee',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
          }),

        name: Yup.string().nullable(),
        amount: Yup.number().required(),
        costCenterCode: Yup.string().nullable(),
        projectId: Yup.string().nullable(),
        companyId: Yup.string().nullable(),
        reference: Yup.string().nullable(),
        ...(jokerLineFields.length > 0
          ? Object.fromEntries(
              jokerLineFields.map((field) => {
                const fieldType = getJokerFieldType(field);
                return [
                  field.field,
                  fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
                ];
              })
            )
          : {}),
      })
    ),
  });

  const insertObject = (values) => {
    const insertObject = {
      ...cleanValues(values),
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      createdBy: user.userId,
      otherExpenseLine: values.otherExpenseLine.map((oE) => cleanValues(oE)),
    };

    otherExpenseService
      .createRow(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);
        setRowSelectionModel(data.id);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const updateObject = (values) => {
    const updData = {
      ...cleanValues(values),
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      updatedBy: user.userId,
      otherExpenseLine: values.otherExpenseLine.map((oE) => cleanValues(oE)),
    };

    otherExpenseService.updateRow(updData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      insertObject(values);
    }
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    otherExpenseService.deleteRow(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  return (
    <>
      <FormBase
        name="OtherExpense"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setInitValues(defaultValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <OtherExpenseFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autocompleteOptions={autocompleteOptions}
                      autoCompleteData={autoCompleteData}
                    />

                    {!isLoading && (
                      <OtherExpenseTabs
                        setRefetchTable={setRefetchTable}
                        user={user}
                        disabled={viewField}
                        defaultField={defaultOtherExpenseLine}
                        autocompleteOptions={autocompleteOptions}
                        autoCompleteData={autoCompleteData}
                        jokerFields={jokerFields}
                        jokerLineFields={jokerLineFields}
                      />
                    )}

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);
                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <>
            <OtherExpenseReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              autocompleteOptions={autocompleteOptions}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
              jokerFields={jokerFields}
            />
          </>
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
